/*
* @Author: Gang Jiang
* @Date: 2026-05-25 11:08:09
* @Last Modified by: Gang Jiang
 * @Last Modified time: 2026-06-03 17:13:52
*/
<template>
  <div ref="zjdgx" class="influencer-reports">
    <div class="bread-crumb">
      Reports
    </div>

    <div class="page-head">
      <h2>{{ pageTitle }}</h2>
      <a-radio-group v-model:value="periodType" class="period-toggle" button-style="solid" @change="onPeriodChange">
        <a-radio-button value="daily">
          Daily
        </a-radio-button>
        <a-radio-button value="monthly">
          Monthly
        </a-radio-button>
      </a-radio-group>
    </div>

    <a-tabs v-model:activeKey="activeTab" @change="onTabChange">
      <a-tab-pane key="influencer" tab="Influencer Report">
        <div class="content">
          <a-spin :spinning="isLoading">
            <div class="filter-panel">
              <a-form :model="filter" layout="horizontal" :colon="true" class="filter-panel__form">
                <div class="filter-panel__field filter-panel__field--date">
                  <a-form-item
                    :label="periodType === 'daily' ? 'Date Range' : 'Month Range'"
                    :label-col="FILTER_LABEL_COL"
                    :wrapper-col="FILTER_WRAPPER_COL"
                  >
                    <a-range-picker
                      v-model:value="filterDate"
                      :picker="periodType === 'monthly' ? 'month' : 'date'"
                      :style="{ width: '100%' }"
                      :disabled-date="disabledDate"
                      :format="periodType === 'daily' ? 'MMM D, YYYY' : 'MMM YYYY'"
                      :allow-clear="false"
                      @calendarChange="onFilterRangeCalendarChange"
                      @openChange="onFilterRangeOpenChange"
                      @change="onDateChange"
                    />
                  </a-form-item>
                </div>
                <div class="filter-panel__field">
                  <a-form-item label="Influencer" :label-col="FILTER_LABEL_COL" :wrapper-col="FILTER_WRAPPER_COL">
                    <a-auto-complete v-model:value="filter.influencerUsername" :allow-clear="true"
                      placeholder="Search username..." :options="userSuggestions" @select="onUserSelect"
                      @clear="clearUser" @search="onUserSearch">
                      <template #prefix>
                        <SearchOutlined />
                      </template>
                    </a-auto-complete>
                  </a-form-item>
                </div>
                <div class="filter-panel__field">
                  <a-form-item label="Advertiser" :label-col="FILTER_LABEL_COL" :wrapper-col="FILTER_WRAPPER_COL">
                    <a-select v-model:value="filter.advertiserId" show-search allow-clear placeholder="All Advertisers"
                      :filter-option="false" :not-found-content="advertiserState.fetching ? undefined : null"
                      :options="advertiserOptions" @search="onAdvertiserSearch" @clear="onAdvertiserClear">
                      <template #suffixIcon>
                        <FilterOutlined />
                      </template>
                      <template v-if="advertiserState.fetching" #notFoundContent>
                        <a-spin size="small" />
                      </template>
                    </a-select>
                  </a-form-item>
                </div>
                <div class="filter-panel__search-col">
                  <a-button type="primary" class="filter-panel__search-btn" @click="searchReports">
                    Search
                  </a-button>
                  <a-button type="default" @click="resetSearch">Reset</a-button>
                  <a :href="exportUrl" download>
                    <a-button type="primary">Export</a-button>
                  </a>
                </div>
              </a-form>
            </div>

            <div class="report-table-wrap">
            <a-table class="report-table" size="small" row-key="rowKey" :columns="influencerTableColumns"
              :data-source="reportList" :pagination="false" v-model:expanded-row-keys="expandedRowKeys"
              :expand-column-width="REPORT_EXPAND_COLUMN_WIDTH"
              :row-expandable="(record) => Number(record.advertiserCount) > 0"
              table-layout="fixed">
              <template #expandColumnTitle>
                Advertisers
              </template>

              <template #expandIcon="{ expanded, record, expandable }">
                <button v-if="expandable" type="button" class="advertisers-expand-trigger" :aria-expanded="expanded"
                  :aria-label="expanded ? 'Collapse advertisers' : 'Expand advertisers'"
                  @click.stop="toggleReportRowExpand(record, expanded)">
                  <span class="report-expand-icon" :class="{ 'is-expanded': expanded }" aria-hidden="true">
                    <svg width="12" height="14" viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2 4.5L6 1.5L10 4.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"
                        stroke-linejoin="round" />
                      <path d="M2 9.5L6 12.5L10 9.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"
                        stroke-linejoin="round" />
                    </svg>
                  </span>
                  <span class="advertiser-count">({{ record.advertiserCount ?? 0 }})</span>
                </button>
                <span v-else class="advertiser-count advertiser-count--static">({{ record.advertiserCount ?? 0
                  }})</span>
              </template>

              <template #expandedRowRender="{ record }">
                <div class="advertiser-breakdown">
                  <h4 class="advertiser-breakdown__title">
                    Breakdown by Advertiser
                  </h4>
                  <div class="advertiser-breakdown__panel">
                    <a-spin :spinning="!!record.breakdownLoading && !record.breakdownLoadingMore">
                      <div
                        class="advertiser-breakdown__scroll"
                        @scroll="(e) => onBreakdownScroll(e, record)"
                      >
                        <a-table
                          class="advertiser-breakdown__table"
                          :columns="INFLUENCER_BREAKDOWN_COLUMNS"
                          :data-source="record.advertisers || []"
                          :pagination="false"
                          size="small"
                          row-key="advertiserName"
                          :show-header="true"
                          :bordered="false"
                        >
                          <template #bodyCell="{ column, record: adv }">
                            <template v-if="column.dataIndex === 'advertiserName'">
                              {{ adv.advertiserName }}
                            </template>
                            <template v-else-if="column.dataIndex === 'grossSales' || column.dataIndex === 'commissions'">
                              <span class="advertiser-breakdown__cell-num">{{ formatCurrency(adv[column.dataIndex])
                                }}</span>
                            </template>
                            <template v-else-if="column.dataIndex === 'clicks'">
                              <span class="advertiser-breakdown__cell-num">{{ formatNumber(adv.clicks) }}</span>
                            </template>
                          </template>
                        </a-table>
                        <div
                          v-if="record.breakdownLoadingMore"
                          class="advertiser-breakdown__load-more"
                        >
                          <a-spin size="small" />
                          <span>Loading more...</span>
                        </div>
                        <div
                          v-else-if="showBreakdownNoMore(record)"
                          class="advertiser-breakdown__no-more"
                        >
                          No more
                        </div>
                      </div>
                    </a-spin>
                  </div>
                </div>
              </template>

              <template #bodyCell="{ column, record }">
                <template v-if="column.key === 'influencerUsername'">
                  <a class="username-link" href="javascript:;" @click="goInfluencer(record.influencerId)">{{
                    record.influencerUsername }}</a>
                </template>
                <template v-else-if="column.dataIndex === 'grossSales' || column.dataIndex === 'commissions'">
                  {{ formatCurrency(record[column.dataIndex]) }}
                </template>
                <template v-else-if="column.dataIndex === 'clicks'">
                  {{ formatNumber(record.clicks) }}
                </template>
                <template v-else-if="column.dataIndex === 'cr'">
                  {{ formatPercent(record.cr) }}
                </template>
              </template>

              <template #summary>
                <a-table-summary-row class="report-summary-row">
                  <a-table-summary-cell :index="0">
                    Total
                  </a-table-summary-cell>
                  <a-table-summary-cell :index="1" />
                  <a-table-summary-cell :index="2" />
                  <a-table-summary-cell :index="3" align="right">
                    {{ formatCurrency(summary.grossSales) }}
                  </a-table-summary-cell>
                  <a-table-summary-cell :index="4" align="right">
                    {{ formatCurrency(summary.commissions) }}
                  </a-table-summary-cell>
                  <a-table-summary-cell :index="5" align="right">
                    {{ formatNumber(summary.clicks) }}
                  </a-table-summary-cell>
                  <a-table-summary-cell :index="6" align="right">
                    {{ formatPercent(summary.cr) }}
                  </a-table-summary-cell>
                  <a-table-summary-cell :index="7" />
                  <a-table-summary-cell :index="8" />
                </a-table-summary-row>
              </template>
            </a-table>
            </div>

            <div class="table-footer">
              <span>{{ paginationHint }}</span>
              <a-pagination v-model:current="filter.page" :total="pagination.total" :page-size="filter.size"
                :show-size-changer="false" @change="onPageChange" />
            </div>
          </a-spin>
        </div>
      </a-tab-pane>
      <a-tab-pane key="advertiser" tab="Advertiser Report">
        <div class="content">
          <a-spin :spinning="isLoading">
            <div class="filter-panel">
              <a-form :model="filter" layout="horizontal" :colon="true" class="filter-panel__form">
                <div class="filter-panel__field filter-panel__field--date">
                  <a-form-item
                    :label="periodType === 'daily' ? 'Date Range' : 'Month Range'"
                    :label-col="FILTER_LABEL_COL"
                    :wrapper-col="FILTER_WRAPPER_COL"
                  >
                    <a-range-picker
                      v-model:value="filterDate"
                      :picker="periodType === 'monthly' ? 'month' : 'date'"
                      :style="{ width: '100%' }"
                      :disabled-date="disabledDate"
                      :format="periodType === 'daily' ? 'MMM D, YYYY' : 'MMM YYYY'"
                      :allow-clear="false"
                      @calendarChange="onFilterRangeCalendarChange"
                      @openChange="onFilterRangeOpenChange"
                      @change="onDateChange"
                    />
                  </a-form-item>
                </div>
                <div class="filter-panel__field">
                  <a-form-item label="Influencer" :label-col="FILTER_LABEL_COL" :wrapper-col="FILTER_WRAPPER_COL">
                    <a-auto-complete v-model:value="filter.influencerUsername" :allow-clear="true"
                      placeholder="Search username..." :options="userSuggestions" @select="onUserSelect"
                      @clear="clearUser" @search="onUserSearch">
                      <template #prefix>
                        <SearchOutlined />
                      </template>
                    </a-auto-complete>
                  </a-form-item>
                </div>
                <div class="filter-panel__field">
                  <a-form-item label="Advertiser" :label-col="FILTER_LABEL_COL" :wrapper-col="FILTER_WRAPPER_COL">
                    <a-select v-model:value="filter.advertiserId" show-search allow-clear placeholder="All Advertisers"
                      :filter-option="false" :not-found-content="advertiserState.fetching ? undefined : null"
                      :options="advertiserOptions" @search="onAdvertiserSearch" @clear="onAdvertiserClear">
                      <template #suffixIcon>
                        <FilterOutlined />
                      </template>
                      <template v-if="advertiserState.fetching" #notFoundContent>
                        <a-spin size="small" />
                      </template>
                    </a-select>
                  </a-form-item>
                </div>
                <div class="filter-panel__search-col">
                  <a-button type="primary" class="filter-panel__search-btn" @click="searchReports">
                    Search
                  </a-button>
                  <a-button type="default" @click="resetSearch">Reset</a-button>
                  <a :href="exportUrl" download>
                    <a-button type="primary">Export</a-button>
                  </a>
                </div>
              </a-form>
            </div>

          <div class="report-table-wrap">
          <a-table class="report-table" size="small" row-key="rowKey" :columns="advertiserTableColumns"
              :data-source="reportList" :pagination="false" v-model:expanded-row-keys="expandedRowKeys"
              :expand-column-width="REPORT_EXPAND_COLUMN_WIDTH"
              :row-expandable="(record) => Number(record.influencerCount) > 0"
              table-layout="fixed">
              <template #expandColumnTitle>
                Influencers
              </template>

              <template #expandIcon="{ expanded, record, expandable }">
                <button v-if="expandable" type="button" class="advertisers-expand-trigger" :aria-expanded="expanded"
                  :aria-label="expanded ? 'Collapse influencers' : 'Expand influencers'"
                  @click.stop="toggleReportRowExpand(record, expanded)">
                  <span class="report-expand-icon" :class="{ 'is-expanded': expanded }" aria-hidden="true">
                    <svg width="12" height="14" viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2 4.5L6 1.5L10 4.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"
                        stroke-linejoin="round" />
                      <path d="M2 9.5L6 12.5L10 9.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"
                        stroke-linejoin="round" />
                    </svg>
                  </span>
                  <span class="advertiser-count">({{ record.influencerCount ?? 0 }})</span>
                </button>
                <span v-else class="advertiser-count advertiser-count--static">({{ record.influencerCount ?? 0
                  }})</span>
              </template>

              <template #expandedRowRender="{ record }">
                <div class="advertiser-breakdown">
                  <h4 class="advertiser-breakdown__title">
                    Breakdown by Influencer
                  </h4>
                  <div class="advertiser-breakdown__panel">
                    <a-spin :spinning="!!record.breakdownLoading && !record.breakdownLoadingMore">
                      <div
                        class="advertiser-breakdown__scroll"
                        @scroll="(e) => onBreakdownScroll(e, record)"
                      >
                        <a-table
                          class="advertiser-breakdown__table"
                          :columns="ADVERTISER_BREAKDOWN_COLUMNS"
                          :data-source="record.influencers || []"
                          :pagination="false"
                          size="small"
                          row-key="influencerUsername"
                          :show-header="true"
                          :bordered="false"
                        >
                          <template #bodyCell="{ column, record: adv }">
                            <template v-if="column.dataIndex === 'influencerUsername'">
                              <a class="username-link" href="javascript:;" @click="goInfluencer(adv.influencerId)">{{
                                adv.influencerUsername }}</a>
                            </template>
                            <template v-else-if="column.dataIndex === 'grossSales' || column.dataIndex === 'commissions'">
                              <span class="advertiser-breakdown__cell-num">{{ formatCurrency(adv[column.dataIndex])
                                }}</span>
                            </template>
                            <template v-else-if="column.dataIndex === 'clicks'">
                              <span class="advertiser-breakdown__cell-num">{{ formatNumber(adv.clicks) }}</span>
                            </template>
                          </template>
                        </a-table>
                        <div
                          v-if="record.breakdownLoadingMore"
                          class="advertiser-breakdown__load-more"
                        >
                          <a-spin size="small" />
                          <span>Loading more...</span>
                        </div>
                        <div
                          v-else-if="showBreakdownNoMore(record)"
                          class="advertiser-breakdown__no-more"
                        >
                          No more
                        </div>
                      </div>
                    </a-spin>
                  </div>
                </div>
              </template>

              <template #bodyCell="{ column, record }">
                <template v-if="column.dataIndex === 'grossSales' || column.dataIndex === 'commissions'">
                  {{ formatCurrency(record[column.dataIndex]) }}
                </template>
                <template v-else-if="column.dataIndex === 'clicks'">
                  {{ formatNumber(record.clicks) }}
                </template>
                <template v-else-if="column.dataIndex === 'cr'">
                  {{ formatPercent(record.cr) }}
                </template>
              </template>

              <template #summary>
                <a-table-summary-row class="report-summary-row">
                  <a-table-summary-cell :index="0">
                    Total
                  </a-table-summary-cell>
                  <a-table-summary-cell :index="1" />
                  <a-table-summary-cell :index="2" />
                  <a-table-summary-cell :index="3" align="right">
                    {{ formatCurrency(summary.grossSales) }}
                  </a-table-summary-cell>
                  <a-table-summary-cell :index="4" align="right">
                    {{ formatCurrency(summary.commissions) }}
                  </a-table-summary-cell>
                  <a-table-summary-cell :index="5" align="right">
                    {{ formatNumber(summary.clicks) }}
                  </a-table-summary-cell>
                  <a-table-summary-cell :index="6" align="right">
                    {{ formatPercent(summary.cr) }}
                  </a-table-summary-cell>
                  <a-table-summary-cell :index="7" />
                </a-table-summary-row>
              </template>
            </a-table>
            </div>

            <div class="table-footer">
              <span>{{ paginationHint }}</span>
              <a-pagination v-model:current="filter.page" :total="pagination.total" :page-size="filter.size"
                :show-size-changer="false" @change="onPageChange" />
            </div>
          </a-spin>
        </div>
      </a-tab-pane>
    </a-tabs>
  </div>
</template>

<script setup>
import moment from 'moment'
import { debounce } from 'lodash-es'
import { computed, onMounted, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import { message } from 'ant-design-vue'
import {
  SearchOutlined,
  FilterOutlined,
} from '@ant-design/icons-vue'
import { apiDomain } from '../../../config'
import { user, advertiser, reports } from '../../server/index'
import {
  INFLUENCER_TABLE_COLUMNS,
  ADVERTISER_TABLE_COLUMNS,
  INFLUENCER_BREAKDOWN_COLUMNS,
  ADVERTISER_BREAKDOWN_COLUMNS,
  REPORT_EXPAND_COLUMN_WIDTH,
} from './utils'
import {
  MOCK_REPORT_LIST,
  MOCK_REPORT_SUMMARY,
  MOCK_REPORT_TOTAL,
  MOCK_ADVERTISER_REPORT_LIST,
  MOCK_ADVERTISER_REPORT_TOTAL,
} from './mock'

/** 暂时使用 mock 数据；接口就绪后改为 false */
const USE_MOCK_REPORT_DATA = false

/** 展开明细表每页条数 */
const BREAKDOWN_PAGE_SIZE = 20

/** 日报默认区间天数（含今天） */
const REPORT_DEFAULT_RANGE_DAYS = 30
/** 日期区间上限：两年（天，含起止） */
const REPORT_MAX_RANGE_DAYS = 365 * 2

const createBreakdownState = () => ({
  breakdownPage: 0,
  breakdownTotal: 0,
  breakdownHasMore: false,
  breakdownLoadingMore: false,
  breakdownLoaded: false,
  breakdownLoading: false,
})

/** 筛选区横向布局：标签与输入框同一行垂直居中 */
const FILTER_LABEL_COL = { flex: '0 0 auto' }
const FILTER_WRAPPER_COL = { flex: '1 1 0', style: { minWidth: 0 } }

const router = useRouter()
const isLoading = ref(false)
const periodType = ref('daily')
const activeTab = ref('influencer')

function withPeriodDateColumnTitle(columns, period) {
  return columns.map((col, index) => {
    if (index !== 0 || col.dataIndex !== 'date') return col
    return {
      ...col,
      title: period === 'monthly' ? 'Month' : 'Date',
    }
  })
}

const influencerTableColumns = computed(() =>
  withPeriodDateColumnTitle(INFLUENCER_TABLE_COLUMNS, periodType.value)
)

const advertiserTableColumns = computed(() =>
  withPeriodDateColumnTitle(ADVERTISER_TABLE_COLUMNS, periodType.value)
)

function getMaxSelectableEnd(period = periodType.value) {
  return period === 'monthly' ? moment().endOf('month') : moment().endOf('day')
}

/** 最早可选日期：距今两年内 */
function getMinSelectableStart(period = periodType.value) {
  const min = moment().subtract(2, 'years')
  return period === 'monthly' ? min.startOf('month') : min.startOf('day')
}

function countRangeDays(start, end, period = periodType.value) {
  if (period === 'monthly') {
    return end.clone().endOf('month').diff(start.clone().startOf('month'), 'days') + 1
  }
  return end.clone().startOf('day').diff(start.clone().startOf('day'), 'days') + 1
}

function getDefaultFilterDate(period = periodType.value) {
  const maxEnd = getMaxSelectableEnd(period)
  if (period === 'monthly') {
    let start = moment().subtract(5, 'months').startOf('month')
    const end = maxEnd.clone()
    if (countRangeDays(start, end, 'monthly') > REPORT_MAX_RANGE_DAYS) {
      start = end.clone().subtract(REPORT_MAX_RANGE_DAYS - 1, 'days').startOf('month')
    }
    return [start, end]
  }
  return [
    moment().subtract(REPORT_DEFAULT_RANGE_DAYS - 1, 'days').startOf('day'),
    maxEnd,
  ]
}

const filterDate = ref(getDefaultFilterDate('daily'))
/** 日期面板选择过程中的区间，供 disabledDate 限制两年 */
const pickingRange = ref([])
const expandedRowKeys = ref([])
const reportList = ref([])
const summary = ref({
  grossSales: 0,
  commissions: 0,
  clicks: 0,
  cr: 0,
})
const pagination = ref({ total: 0 })
const userSuggestions = ref([])
const advertiserState = reactive({
  fetching: false,
  data: [],
})
const filter = ref({
  page: 1,
  size: 50,
  startTime: filterDate.value[0].startOf('day').valueOf(),
  endTime: filterDate.value[1].endOf('day').valueOf(),
  influencerSuggestionId: null,
  influencerUsername: null,
  advertiserId: null,
  sort: undefined,
})

const pageTitle = computed(() =>
  activeTab.value === 'influencer'
    ? periodType.value === 'daily'
      ? 'Influencer Report (Daily)'
      : 'Influencer Report (Monthly)'
    : periodType.value === 'daily'
      ? 'Advertiser Report (Daily)'
      : 'Advertiser Report (Monthly)'
)

const advertiserOptions = computed(() => [
  { label: 'All Advertisers', value: '' },
  ...advertiserState.data,
])

const paginationHint = computed(() => {
  const total = pagination.value.total || 0
  if (!total) return 'Showing 0 entries'
  const start = (filter.value.page - 1) * filter.value.size + 1
  const end = Math.min(filter.value.page * filter.value.size, total)
  return `Showing ${start} to ${end} of ${total} entries`
})

function disabledDate(current) {
  if (!current) return false
  const m = moment(current)
  const period = periodType.value
  const maxEnd = getMaxSelectableEnd(period)
  const minStart = getMinSelectableStart(period)
  const unit = period === 'monthly' ? 'month' : 'day'

  // 超过今天 / 本月，或早于两年前，不可选
  if (m.isAfter(maxEnd, unit) || m.isBefore(minStart, unit)) return true

  const pick = pickingRange.value || []
  const anchor = pick[0] || pick[1]
  if (!anchor) return false

  const anchorDay = moment(anchor).startOf(unit)
  const min = moment.max(
    anchorDay.clone().subtract(REPORT_MAX_RANGE_DAYS - 1, 'days'),
    minStart,
  )
  const max = moment.min(
    anchorDay.clone().add(REPORT_MAX_RANGE_DAYS - 1, 'days'),
    maxEnd,
  )
  const cur = m.clone().startOf(unit)
  return cur.isBefore(min, 'day') || cur.isAfter(max, 'day')
}

function normalizeFilterDateRange(dates) {
  if (!Array.isArray(dates) || dates.length < 2 || !dates[0] || !dates[1]) {
    return null
  }
  const period = periodType.value
  let start = dates[0].clone()
  let end = dates[1].clone()
  const maxEnd = getMaxSelectableEnd(period)
  const minStart = getMinSelectableStart(period)
  let adjusted = false

  if (period === 'monthly') {
    start = start.startOf('month')
    end = end.endOf('month')
  } else {
    start = start.startOf('day')
    end = end.endOf('day')
  }

  if (start.isBefore(minStart)) {
    start = minStart.clone()
    adjusted = true
  }
  if (end.isAfter(maxEnd)) {
    end = maxEnd.clone()
    adjusted = true
  }
  if (start.isAfter(end)) {
    start = period === 'monthly' ? end.clone().startOf('month') : end.clone().startOf('day')
    adjusted = true
  }

  if (countRangeDays(start, end, period) > REPORT_MAX_RANGE_DAYS) {
    end = start.clone().add(REPORT_MAX_RANGE_DAYS - 1, 'days')
    end = period === 'monthly' ? end.endOf('month') : end.endOf('day')
    if (end.isAfter(maxEnd)) end = maxEnd.clone()
    adjusted = true
    message.warning('Date range cannot exceed 2 years.')
  }

  return adjusted ? [start, end] : [start, end]
}

function onFilterRangeCalendarChange(val) {
  pickingRange.value = val && val.length ? [...val] : []
}

function onFilterRangeOpenChange(open) {
  if (!open) pickingRange.value = []
}

function syncFilterTimeFromDateRange(dates) {
  if (!Array.isArray(dates) || dates.length < 2 || !dates[0] || !dates[1]) {
    return false
  }
  if (periodType.value === 'monthly') {
    filter.value.startTime = dates[0].clone().startOf('month').valueOf()
    filter.value.endTime = dates[1].clone().endOf('month').valueOf()
  } else {
    filter.value.startTime = dates[0].clone().startOf('day').valueOf()
    filter.value.endTime = dates[1].clone().endOf('day').valueOf()
  }
  return true
}

function formatCurrency(value) {
  const num = Number(value)
  if (Number.isNaN(num)) return '-'
  return `$${num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

function formatNumber(value) {
  const num = Number(value)
  if (Number.isNaN(num)) return '-'
  return num.toLocaleString('en-US')
}

function formatPercent(value) {
  const num = Number(value)
  if (Number.isNaN(num)) return '-'
  return `${num}%`
}

/** 解析 trend（接口可能返回数字或字符串） */
function parseTrend(value) {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value
  }
  if (typeof value === 'string' && value.trim()) {
    const num = parseFloat(value.replace('%', ''))
    return Number.isNaN(num) ? 0 : num
  }
  return 0
}

function formatReportDate(item, period) {
  if (period === 'monthly') {
    return moment(item.startTime).format('YYYY-MM')
  }
  return moment(item.startTime).format('YYYY-MM-DD')
}

/** 将明细行按网红 + 周期聚合，生成可展开的广告主列表 */
function groupInfluencerPeriodRows(rows) {
  const groups = new Map()

  rows.forEach((item) => {
    const key = `${item.startTime}-${item.endTime}-${item.influencerId}`

    if (!groups.has(key)) {
      groups.set(key, {
        startTime: item.startTime,
        endTime: item.endTime,
        influencerId: item.influencerId,
        influencerUsername: item.influencerUsername,
        grossSales: 0,
        commissions: 0,
        clicks: 0,
        cr: 0,
        trend: parseTrend(item.trend),
        advertiserCount: item.advertiserCount,
        advertisers: [],
        _crSum: 0,
        _crCount: 0,
      })
    }

    const group = groups.get(key)
    const hasAdvertiserDetail =
      item.advertiserId != null || Boolean(item.advertiserName)

    if (hasAdvertiserDetail) {
      group.grossSales += Number(item.grossSales) || 0
      group.commissions += Number(item.commissions) || 0
      group.clicks += Number(item.clicks) || 0
      if (item.cr != null && item.cr !== '') {
        group._crSum += Number(item.cr) || 0
        group._crCount += 1
      }
    } else {
      group.grossSales = Number(item.grossSales) || 0
      group.commissions = Number(item.commissions) || 0
      group.clicks = Number(item.clicks) || 0
      group.cr = Number(item.cr) || 0
      group.advertiserCount = item.advertiserCount ?? 0
    }

    if (item.advertiserCount != null && hasAdvertiserDetail) {
      group.advertiserCount = item.advertiserCount
    }
    if (item.trend != null && item.trend !== '') {
      group.trend = parseTrend(item.trend)
    }
  })

  return Array.from(groups.values()).map((group) => {
    if (group._crCount > 0) {
      group.cr = group._crSum / group._crCount
    }
    delete group._crSum
    delete group._crCount
    return group
  })
}

function normalizeListItem(item, period, index) {
  return {
    rowKey: `${item.startTime}-${item.endTime}-${item.influencerId}-${index}`,
    date: formatReportDate(item, period),
    startTime: item.startTime,
    endTime: item.endTime,
    influencerUsername: item.influencerUsername,
    influencerId: item.influencerId,
    grossSales: item.grossSales,
    commissions: item.commissions,
    clicks: item.clicks,
    cr: item.cr,
    trend: parseTrend(item.trend),
    advertiserCount: item.advertiserCount ?? 0,
    advertisers: [],
    breakdownLoaded: false,
    breakdownLoading: false,
  }
}

function normalizeAdvertiserListItem(item, period, index) {
  return {
    rowKey: `${item.startTime}-${item.endTime}-${item.advertiserId}-${index}`,
    date: formatReportDate(item, period),
    startTime: item.startTime,
    endTime: item.endTime,
    advertiserName: item.advertiserName,
    advertiserId: item.advertiserId,
    grossSales: item.grossSales,
    commissions: item.commissions,
    clicks: item.clicks,
    cr: item.cr,
    trend: parseTrend(item.trend),
    influencerCount: item.influencerCount ?? 0,
    influencers: [],
    ...createBreakdownState(),
  }
}

function normalizeAdvertiserBreakdownRows(rows) {
  return rows
    .filter((item) => !item.totalRow)
    .map((item) => ({
      advertiserName: item.advertiserName || String(item.advertiserId ?? ''),
      grossSales: item.grossSales,
      commissions: item.commissions,
      clicks: item.clicks,
      cr: item.cr,
    }))
}

function normalizeInfluencerBreakdownRows(rows) {
  return rows
    .filter((item) => !item.totalRow)
    .map((item) => ({
      influencerUsername: item.influencerUsername,
      influencerId: item.influencerId,
      grossSales: item.grossSales,
      commissions: item.commissions,
      clicks: item.clicks,
      cr: item.cr,
    }))
}

function updateReportRow(rowKey, patch) {
  const index = reportList.value.findIndex((row) => row.rowKey === rowKey)
  if (index === -1) return
  reportList.value[index] = {
    ...reportList.value[index],
    ...patch,
  }
}

function buildBreakdownParams(record, page = 1) {
  const params = {
    period: periodType.value === 'daily' ? 'day' : 'month',
    startTime: record.startTime,
    endTime: record.endTime,
    page,
    size: BREAKDOWN_PAGE_SIZE,
  }
  if (activeTab.value === 'influencer') {
    params.influencerId = record.influencerId
    if (filter.value.advertiserId) {
      params.advertiserId = filter.value.advertiserId
    }
  } else {
    params.advertiserId = record.advertiserId
    if (filter.value.influencerSuggestionId) {
      params.influencerId = filter.value.influencerSuggestionId
    }
  }
  
  return params
}

function getMockBreakdownList(record) {
  if (activeTab.value === 'influencer') {
    const mockRow = MOCK_REPORT_LIST.find((item) => item.rowKey === record.rowKey)
    return mockRow?.advertisers || []
  }
  const mockRow = MOCK_ADVERTISER_REPORT_LIST.find((item) => item.rowKey === record.rowKey)
  return mockRow?.influencers || []
}

function resolveBreakdownTotal(res, mergedLength, pageListLength) {
  const total = res?.total ?? res?.dataTotal
  if (total != null && total !== '') {
    return Number(total)
  }
  if (pageListLength < BREAKDOWN_PAGE_SIZE) {
    return mergedLength
  }
  return null
}

function resolveBreakdownHasMore(mergedLength, total, pageListLength) {
  if (total != null && !Number.isNaN(total)) {
    return mergedLength < total
  }
  return pageListLength >= BREAKDOWN_PAGE_SIZE
}

function fetchRowBreakdown(record, { append = false } = {}) {
  if (!record?.rowKey) return

  if (append) {
    if (
      record.breakdownLoading ||
      record.breakdownLoadingMore ||
      !record.breakdownHasMore
    ) {
      return
    }
  } else if (record.breakdownLoading || record.breakdownLoaded) {
    return
  }

  const rowKey = record.rowKey
  const isInfluencerTab = activeTab.value === 'influencer'
  const listKey = isInfluencerTab ? 'advertisers' : 'influencers'
  const nextPage = append ? (record.breakdownPage || 1) + 1 : 1

  updateReportRow(
    rowKey,
    append ? { breakdownLoadingMore: true } : { breakdownLoading: true }
  )

  const finishBreakdown = (pageList, total, errorMessage) => {
    const current = reportList.value.find((row) => row.rowKey === rowKey)
    if (!current) return

    if (errorMessage) {
      message.error(errorMessage)
      updateReportRow(rowKey, {
        breakdownLoading: false,
        breakdownLoadingMore: false,
      })
      return
    }

    const prevList = append ? current[listKey] || [] : []
    const merged = append ? [...prevList, ...pageList] : pageList
    const totalSource =
      total != null && typeof total === 'object'
        ? total
        : total != null
          ? { total }
          : null
    const resolvedTotal = resolveBreakdownTotal(
      totalSource,
      merged.length,
      pageList.length
    )
    const hasMore = resolveBreakdownHasMore(
      merged.length,
      resolvedTotal,
      pageList.length
    )

    updateReportRow(rowKey, {
      [listKey]: merged,
      breakdownPage: nextPage,
      breakdownTotal: resolvedTotal ?? merged.length,
      breakdownHasMore: hasMore,
      breakdownLoaded: true,
      breakdownLoading: false,
      breakdownLoadingMore: false,
    })
  }

  if (USE_MOCK_REPORT_DATA) {
    const full = getMockBreakdownList(record)
    const start = (nextPage - 1) * BREAKDOWN_PAGE_SIZE
    const slice = full.slice(start, start + BREAKDOWN_PAGE_SIZE)
    window.setTimeout(() => {
      finishBreakdown(slice, { total: full.length })
    }, 200)
    return
  }

  const request = isInfluencerTab
    ? reports.getAdvertiserReportsByInflencer(buildBreakdownParams(record, nextPage))
    : reports.getInfluencerReportsByAdvertiser(buildBreakdownParams(record, nextPage))

  request
    .then((res) => {
      if (!res?.success) {
        finishBreakdown([], null, res?.error || 'Failed to load breakdown')
        return
      }
      const rows = Array.isArray(res.data)
        ? res.data
        : (res.data?.list || res.data?.records || [])
      const list = isInfluencerTab
        ? normalizeAdvertiserBreakdownRows(rows)
        : normalizeInfluencerBreakdownRows(rows)
      finishBreakdown(list, res)
    })
    .catch((err) => {
      finishBreakdown(
        [],
        null,
        err?.response?.data?.error || err?.message || 'Failed to load breakdown'
      )
    })
}

function onBreakdownScroll(event, record) {
  const el = event?.target
  if (!el) return
  const threshold = 40
  if (el.scrollTop + el.clientHeight < el.scrollHeight - threshold) {
    return
  }
  fetchRowBreakdown(record, { append: true })
}

function showBreakdownNoMore(record) {
  return !!(
    record?.breakdownLoaded &&
    !record?.breakdownHasMore &&
    !record?.breakdownLoading &&
    !record?.breakdownLoadingMore
  )
}

/** 自定义展开图标时手动同步 expandedRowKeys，避免无法收起 */
function toggleReportRowExpand(record, expanded) {
  const key = record?.rowKey
  if (key == null || key === '') return
  if (expanded) {
    expandedRowKeys.value = expandedRowKeys.value.filter((k) => k !== key)
    return
  }
  if (!expandedRowKeys.value.includes(key)) {
    expandedRowKeys.value = [...expandedRowKeys.value, key]
  }
  fetchRowBreakdown(record)
}

function applyMockData() {
  const isInfluencerTab = activeTab.value === 'influencer'
  if (isInfluencerTab) {
    reportList.value = MOCK_REPORT_LIST.map((item) => {
      const date = moment(item.date)
      const unit = periodType.value === 'daily' ? 'day' : 'month'
      return {
        ...item,
        trend: parseTrend(item.trend),
        startTime: date.clone().startOf(unit).valueOf(),
        endTime: date.clone().endOf(unit).valueOf(),
        advertisers: [],
        ...createBreakdownState(),
      }
    })
    pagination.value.total = MOCK_REPORT_TOTAL
  } else {
    reportList.value = MOCK_ADVERTISER_REPORT_LIST.map((item) => {
      const date = moment(item.date)
      const unit = periodType.value === 'daily' ? 'day' : 'month'
      return {
        ...item,
        trend: parseTrend(item.trend),
        startTime: date.clone().startOf(unit).valueOf(),
        endTime: date.clone().endOf(unit).valueOf(),
        influencers: [],
        ...createBreakdownState(),
      }
    })
    pagination.value.total = MOCK_ADVERTISER_REPORT_TOTAL
  }
  summary.value = { ...MOCK_REPORT_SUMMARY }
  expandedRowKeys.value = []
}

function applySummaryData(data) {
  if (!data) {
    summary.value = {
      grossSales: 0,
      commissions: 0,
      clicks: 0,
      cr: 0,
    }
    return
  }
  summary.value = {
    grossSales: data.grossSales ?? 0,
    commissions: data.commissions ?? 0,
    clicks: data.clicks ?? 0,
    cr: data.cr ?? 0,
  }
}

/** 汇总接口查询参数（不含分页） */
function buildSummaryQueryParams() {
  const params = {
    period: periodType.value === 'daily' ? 'day' : 'month',
    startTime: filter.value.startTime,
    endTime: filter.value.endTime,
  }
  if (filter.value.sort) {
    params.sort = filter.value.sort
  }
  if (filter.value.influencerSuggestionId) {
    params.influencerId = filter.value.influencerSuggestionId
  }
  if (filter.value.advertiserId) {
    params.advertiserId = filter.value.advertiserId
  }
  return params
}

function buildQueryParams() {
  const params = {
    page: filter.value.page,
    size: filter.value.size,
    period: periodType.value === 'daily' ? 'day' : 'month',
    startTime: filter.value.startTime,
    endTime: filter.value.endTime,
  }
  if (filter.value.sort) {
    params.sort = filter.value.sort
  }
  if (filter.value.influencerSuggestionId) {
    params.influencerId = filter.value.influencerSuggestionId
  }
  if (filter.value.advertiserId) {
    params.advertiserId = filter.value.advertiserId
  }
  return params
}

function groupAdvertiserPeriodRows(rows) {
  const groups = new Map()

  rows.forEach((item) => {
    const key = `${item.startTime}-${item.endTime}-${item.advertiserId}`

    if (!groups.has(key)) {
      groups.set(key, {
        startTime: item.startTime,
        endTime: item.endTime,
        advertiserId: item.advertiserId,
        advertiserName: item.advertiserName,
        grossSales: 0,
        commissions: 0,
        clicks: 0,
        cr: 0,
        trend: parseTrend(item.trend),
        influencerCount: item.influencerCount ?? 0,
        _crSum: 0,
        _crCount: 0,
      })
    }

    const group = groups.get(key)
    const hasInfluencerDetail =
      item.influencerId != null || Boolean(item.influencerUsername)

    if (hasInfluencerDetail) {
      group.grossSales += Number(item.grossSales) || 0
      group.commissions += Number(item.commissions) || 0
      group.clicks += Number(item.clicks) || 0
      if (item.cr != null && item.cr !== '') {
        group._crSum += Number(item.cr) || 0
        group._crCount += 1
      }
    } else {
      group.grossSales = Number(item.grossSales) || 0
      group.commissions = Number(item.commissions) || 0
      group.clicks = Number(item.clicks) || 0
      group.cr = Number(item.cr) || 0
      group.influencerCount = item.influencerCount ?? 0
    }

    if (item.influencerCount != null && hasInfluencerDetail) {
      group.influencerCount = item.influencerCount
    }
    if (item.trend != null && item.trend !== '') {
      group.trend = parseTrend(item.trend)
    }
  })

  return Array.from(groups.values()).map((group) => {
    if (group._crCount > 0) {
      group.cr = group._crSum / group._crCount
    }
    delete group._crSum
    delete group._crCount
    return group
  })
}

function fetchReports() {
  isLoading.value = true
  expandedRowKeys.value = []

  if (USE_MOCK_REPORT_DATA) {
    window.setTimeout(() => {
      applyMockData()
      isLoading.value = false
    }, 150)
    return
  }

  const params = buildQueryParams()
  const summaryParams = buildSummaryQueryParams()
  const isInfluencerTab = activeTab.value === 'influencer'
  const listRequest = isInfluencerTab
    ? reports.getInfluencerPeriods(params)
    : reports.getAdvertiserPeriods(params)
  const summaryRequest = isInfluencerTab
    ? reports.getInfluencerStatisticsData(summaryParams)
    : reports.getAdvertiserStatisticsData(summaryParams)

  Promise.all([listRequest, summaryRequest])
    .then(([listRes, summaryRes]) => {
      if (!listRes?.success) {
        message.error(listRes?.error || 'Failed to load report')
        reportList.value = []
        pagination.value.total = 0
      } else {
        const rows = Array.isArray(listRes.data) ? listRes.data : []
        const detailRows = rows.filter((item) => !item.totalRow)
        const period = periodType.value === 'daily' ? 'daily' : 'monthly'

        if (isInfluencerTab) {
          const grouped = groupInfluencerPeriodRows(detailRows)
          reportList.value = grouped.map((item, index) =>
            normalizeListItem(item, period, index)
          )
        } else {
          const grouped = groupAdvertiserPeriodRows(detailRows)
          reportList.value = grouped.map((item, index) =>
            normalizeAdvertiserListItem(item, period, index)
          )
        }

        pagination.value.total = listRes.total ?? reportList.value.length
      }

      const summaryPayload = summaryRes?.success ? summaryRes.data : null
      const summaryData = Array.isArray(summaryPayload)
        ? summaryPayload[0]
        : summaryPayload
      applySummaryData(summaryData)
    })
    .catch((err) => {
      message.error(
        err?.response?.data?.error || err?.message || 'Failed to load report'
      )
      reportList.value = []
      pagination.value.total = 0
      applySummaryData(null)
    })
    .finally(() => {
      isLoading.value = false
    })
}

function onTabChange() {
  filter.value.page = 1
  fetchReports()
}

function searchReports() {
  filter.value.page = 1
  fetchReports()
}

/** 重置筛选为默认值并重新查询 */
function resetSearch() {
  pickingRange.value = []
  filterDate.value = getDefaultFilterDate(periodType.value)
  filter.value.page = 1
  filter.value.size = 50
  filter.value.influencerSuggestionId = null
  filter.value.influencerUsername = null
  filter.value.advertiserId = null
  filter.value.sort = undefined
  syncFilterTimeFromDateRange(filterDate.value)
  userSuggestions.value = []
  advertiserState.data = []
  fetchReports()
}

function onPageChange(page) {
  filter.value.page = page
  fetchReports()
}

function onPeriodChange() {
  pickingRange.value = []
  filterDate.value = getDefaultFilterDate(periodType.value)
  syncFilterTimeFromDateRange(filterDate.value)
  filter.value.page = 1
  fetchReports()
}

function onDateChange(dates) {
  pickingRange.value = []
  const normalized = normalizeFilterDateRange(dates)
  if (normalized) {
    const gran = periodType.value === 'monthly' ? 'month' : 'day'
    const changed =
      !dates?.[0]?.isSame(normalized[0], gran) || !dates?.[1]?.isSame(normalized[1], gran)
    if (changed) filterDate.value = normalized
    syncFilterTimeFromDateRange(normalized)
    return
  }
  syncFilterTimeFromDateRange(dates)
}

function onUserSearch(value) {
  value = (value || '').trim()
  if (!value) {
    userSuggestions.value = []
    return
  }
  user
    .getSuggestionList('userName', { userName: value, size: 20 })
    .then((res) => {
      if (res.success) {
        userSuggestions.value = res.data.map((item) => ({
          label: item.userName,
          value: item.id,
        }))
      }
    })
}

function onUserSelect(id, option) {
  filter.value.influencerSuggestionId = id
  filter.value.influencerUsername = option.label
}

function clearUser() {
  filter.value.influencerSuggestionId = null
  filter.value.influencerUsername = null
  userSuggestions.value = []
}

const onAdvertiserSearch = debounce((value) => {
  if (!value) {
    advertiserState.data = []
    return
  }
  advertiserState.fetching = true
  advertiser
    .getSuggestionList({ name: value, size: 20 })
    .then((res) => {
      if (res.success) {
        advertiserState.data = res.data.map((item) => ({
          label: item.advertiserName,
          value: item.id,
        }))
      }
    })
    .finally(() => {
      advertiserState.fetching = false
    })
}, 300)

function onAdvertiserClear() {
  filter.value.advertiserId = null
}

function goInfluencer(influencerId) {
  if (!influencerId) return
  router.push({ path: `/influencer/edit/${influencerId}` })
}

/** 导出查询串（与列表筛选一致，不含分页） */
function buildExportQueryString() {
  const params = buildSummaryQueryParams()
  return Object.keys(params)
    .filter((key) => params[key] !== undefined && params[key] !== null && params[key] !== '')
    .map((key) => `${key}=${encodeURIComponent(params[key])}`)
    .join('&')
}

/** 导出地址：按 Tab 区分 influencer / advertiser 接口 */
const exportUrl = computed(() => {
  const query = buildExportQueryString()
  
  return `https://${apiDomain}/admin/influencer-marketing/v1/report/periods/export${query ? `?${query}&groupInfo=${activeTab.value}_id` : ''}`
})

onMounted(() => {
  fetchReports()
})
</script>

<style scoped lang="less">
/*
 * @Author: Gang Jiang
 * @Date: 2026-05-25 11:08:09
 * @Last Modified by: Gang Jiang
 * @Last Modified time: 2026-05-25 15:53:30
 */

.influencer-reports {
  h2 {
    margin: 0 0 6px;
    font-size: 22px;
    font-weight: 700;
    color: #1a1a1a;
  }

  .page-desc {
    margin: 0;
    font-size: 14px;
    color: #666;
    line-height: 1.5;
  }

  .page-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: -10px;
  }

  .period-toggle {
    flex-shrink: 0;

    .ant-radio-button-wrapper {
      min-width: 88px;
      text-align: center;
      font-weight: 500;
      border-color: #d9d9d9;

      &:first-child {
        border-radius: 8px 0 0 8px;
      }

      &:last-child {
        border-radius: 0 8px 8px 0;
      }
    }

    .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled) {
      background: #1677ff;
      border-color: #1677ff !important;
      color: #fff;

      &::before {
        background-color: #1677ff !important;
      }

      &:hover {
        background: #4096ff;
        border-color: #4096ff !important;
        color: #fff;
      }
    }

    .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled)
      + .ant-radio-button-wrapper::before {
      background-color: #1677ff !important;
    }
  }

  .filter-panel {
    padding: 20px 24px;
    margin-bottom: 20px;
    background: #fafafa;
    border: 1px solid #e8e8e8;
    border-radius: 8px;

    &__form {
      display: flex;
      flex-wrap: nowrap;
      align-items: center;
      gap: 24px;
      width: 100%;
      margin-bottom: 0;
    }

    &__field {
      flex: 1.1 1 180px;
      min-width: 168px;

      &--date {
        flex: 0 0 340px;
        width: 340px;
        min-width: 340px;
        max-width: 340px;

        :deep(.ant-form-item-control) {
          flex: 1 1 auto;
          min-width: 0;
        }

        :deep(.ant-picker.ant-picker-range) {
          width: 100%;
          min-width: 0;
        }

        :deep(.ant-picker-range .ant-picker-input) {
          flex: 1 1 0;
          min-width: 0;
          width: auto;
        }

        :deep(.ant-picker-range .ant-picker-input > input) {
          width: 100%;
          min-width: 0;
          overflow: visible;
          text-overflow: clip;
        }

        :deep(.ant-picker-range .ant-picker-range-separator) {
          flex: 0 0 auto;
          padding: 0 6px;
        }

        :deep(.ant-picker-range .ant-picker-suffix) {
          flex: 0 0 auto;
        }
      }

      :deep(.ant-form-item) {
        margin-bottom: 0;
      }

      :deep(.ant-form-item-row) {
        flex-wrap: nowrap;
        align-items: center;
      }

      :deep(.ant-form-item-label) {
        flex: 0 0 auto;
        padding-bottom: 0;
        padding-right: 8px;
        line-height: 32px;
        white-space: nowrap;

        > label {
          height: 32px;
          font-size: 14px;
          font-weight: 400;
          line-height: 32px;
          color: #333;

          &::after {
            margin-inline: 2px 8px;
          }
        }
      }

      :deep(.ant-form-item-control) {
        flex: 1 1 0;
        min-width: 0;
      }

      :deep(.ant-picker),
      :deep(.ant-select),
      :deep(.ant-input-affix-wrapper) {
        width: 100%;
      }

      :deep(.ant-picker.ant-picker-range),
      :deep(.ant-select-selector),
      :deep(.ant-input-affix-wrapper) {
        display: inline-flex;
        align-items: center;
        height: 32px;
        border-color: #d9d9d9;
        border-radius: 6px;
        background: #fff;
      }

      :deep(.ant-picker-range .ant-picker-input > input),
      :deep(.ant-select-selection-item),
      :deep(.ant-select-selection-placeholder),
      :deep(.ant-input) {
        font-size: 14px;
      }
    }

    &__search-col {
      flex: 0 0 auto;
      margin-left: auto;
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }

  .report-table-wrap {
    width: 100%;
    overflow: hidden;
  }

  .report-table {
    width: 100%;

    :deep(.ant-table-wrapper),
    :deep(.ant-table),
    :deep(.ant-table-container) {
      width: 100%;
    }

    :deep(.ant-table-header table),
    :deep(.ant-table-body table),
    :deep(.ant-table-summary table),
    :deep(.ant-table-content > table) {
      width: 100% !important;
      table-layout: fixed !important;
    }

    :deep(.ant-table-thead > tr > th) {
      background: #eef2fb;
      font-weight: 600;
      color: #333;
    }

    :deep(th.report-col-cr),
    :deep(td.report-col-cr) {
      white-space: nowrap;
    }

    /* 隐藏默认 +/-，Advertisers 列使用自定义双箭头 */
    :deep(.ant-table-row-expand-icon) {
      display: none !important;
    }

    :deep(.ant-table-row-expand-icon-cell),
    :deep(.ant-table-expand-icon-col) {
      width: 14%;
      padding-left: 12px !important;
      text-align: left;
    }

    :deep(.ant-table-expanded-row > .ant-table-cell) {
      padding: 0 !important;
      background: #fff;
    }
  }

  .advertisers-expand-trigger {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 0;
    border: none;
    background: transparent;
    cursor: pointer;
    line-height: 1;
    vertical-align: middle;

    &:hover .report-expand-icon {
      opacity: 0.75;
    }
  }

  /* 设计稿：蓝色上下双 chevron */
  .report-expand-icon {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 12px;
    height: 14px;
    color: #4a90e2;
    line-height: 0;

    svg {
      display: block;
    }
  }

  .username-link {
    color: #4a90e2;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }
  }

  .trend-cell {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-weight: 500;
    font-size: 13px;

    &.up {
      color: #52c41a;
    }

    &.down {
      color: #f5222d;
    }

    &.flat {
      color: #999;
    }
  }

  /* 设计稿：折线趋势图 + 右上角/右下角箭头 */
  .trend-icon {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    line-height: 0;

    svg {
      display: block;
    }
  }

  .advertiser-count {
    color: #4a90e2;
    font-weight: 500;
    font-size: 13px;

    &--static {
      display: inline-block;
    }
  }

  .advertiser-breakdown {
    padding: 16px 20px 20px;
    background: #fff;

    &__title {
      margin: 0 0 12px;
      font-family: Roboto, sans-serif;
      font-size: 14px;
      font-weight: 600;
      line-height: 1.4;
      color: #333;
    }

    &__panel {
      overflow: hidden;
      border: 1px solid #e8e8e8;
      border-radius: 4px;
    }

    &__scroll {
      max-height: 420px;
      overflow-y: auto;
    }

    &__load-more {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 12px 16px;
      font-size: 13px;
      color: rgba(0, 0, 0, 0.45);
      border-top: 1px solid #f0f0f0;
      background: #fff;
    }

    &__no-more {
      padding: 12px 16px;
      font-size: 13px;
      text-align: center;
      color: rgba(0, 0, 0, 0.35);
      border-top: 1px solid #f0f0f0;
      background: #fff;
    }

    &__table {
      :deep(.ant-table) {
        margin: 0 !important;
        background: #fff;
      }

      :deep(.ant-table-container) {
        border: none !important;
      }

      :deep(.ant-table-thead > tr > th) {
        padding: 12px 16px;
        font-size: 13px;
        font-weight: 600;
        color: #666;
        background: #f4f5fb;
        border-bottom: 1px solid #e8e8e8;

        &::before {
          display: none !important;
        }
      }

      :deep(.ant-table-thead > tr > th),
      :deep(.ant-table-tbody > tr > td) {
        border-inline-end: none !important;
      }

      :deep(.ant-table-tbody > tr > td) {
        padding: 12px 16px;
        font-size: 13px;
        color: #333;
        background: #fff;
        border-bottom: 1px solid #f0f0f0;
      }

      :deep(.ant-table-tbody > tr:last-child > td) {
        border-bottom: none;
      }

      :deep(.ant-table-tbody > tr:hover > td) {
        background: #fff;
      }

      :deep(th.report-col-cr),
      :deep(td.report-col-cr) {
        white-space: nowrap;
      }
    }

    &__cell-num {
      font-weight: 700;
      color: #333;
    }
  }

  .table-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 16px;
    padding: 0 4px;
    font-size: 13px;
    color: #666;
  }

  :deep(.report-summary-row) {
    td {
      font-weight: 700;
      background: #f5f5f5 !important;
    }
  }
}

.content {
  padding: 20px;
}
</style>
